import React, { useState, useEffect } from "react";
import {
  Modal,
  Checkbox,
  Button,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

const DownloadExcelModal = ({
  open,
  onClose,
  eventID,
  EventName,
  loggedInUser,
}) => {
  const [tokens, setTokens] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open && eventID) fetchDistinctTokens();
  }, [open, eventID]);

  const fetchDistinctTokens = async () => {
    try {
      setLoadingTokens(true);
      const res = await fetch(
        `${process.env.REACT_APP_PAYMENT_LINK_URL}downloadExcel/getDistinctTokens`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedInUser?.accessToken}`,
          },
          body: JSON.stringify({ eventID }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setTokens(data.tokens);
      } else {
        toast.error("Failed to fetch tokens");
      }
    } catch (err) {
      toast.error("Error fetching tokens");
    } finally {
      setLoadingTokens(false);
    }
  };

  const toggleSelection = (token) => {
    setSelected((prev) =>
      prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]
    );
  };

  const handleDownload = async () => {
    if (selected.length === 0) {
      toast.warning("Please select at least one token");
      return;
    }

    try {
      setDownloading(true);
      const res = await fetch(
        `${process.env.REACT_APP_PAYMENT_LINK_URL}downloadExcel/downloadEventToken`,
        {
          method: "POST",
          body: JSON.stringify({ eventID, tokenNames: selected }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedInUser?.accessToken}`,
          },
        }
      );

      if (!res.ok) {
        toast.error("Failed to download Excel");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Event_${EventName[0]?.title || "Event"}_Launches.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      toast.success("Excel Downloaded Successfully");
      setSelected([]);
      onClose();
    } catch (err) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setSelected([]);
        onClose();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
        backgroundColor: "rgba(0, 0, 0, 0.35)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2.5,
          p: 2.5,
          width: "92%",
          maxWidth: 380,
          boxShadow: 8,
          overflow: "hidden",
          fontFamily: "Rubik, sans-serif",
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{ fontWeight: 600, color: "#333", mb: 1 }}
        >
          Select Tokens to Download
        </Typography>

<Box
  sx={{
    overflowY: "auto",
    maxHeight: "60vh",
    backgroundColor: "#fafafa",
    borderRadius: 1.5,
    border: "1px solid #e5e7eb",
    p: 1.5,
    mt: 1.5,
  }}
>
  {loadingTokens ? (
    <Box display="flex" justifyContent="center" py={5}>
      <CircularProgress size={30} />
    </Box>
  ) : tokens.length === 0 ? (
    <Typography align="center" color="text.secondary" py={3}>
      No tokens found for this event.
    </Typography>
  ) : (
    tokens.map((token) => (
      <Box
        key={token}
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.8,
          px: 1,
          borderRadius: 1,
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: "#f3f4f6" },
        }}
        onClick={() => toggleSelection(token)} // click anywhere on row
      >
        <Checkbox
          checked={selected.includes(token)}
          onClick={(event) => event.stopPropagation()} // ⛔ prevents double toggle
          onChange={() => toggleSelection(token)} // ✅ single toggle on checkbox
          color="primary"
          size="small"
        />
        <Typography sx={{ fontSize: 14, color: "#333" }}>{token}</Typography>
      </Box>
    ))
  )}
</Box>



        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => {
              setSelected([]);
              onClose();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 2,
            }}
            disabled={downloading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleDownload}
            disabled={downloading || loadingTokens}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 2,
              px: 2.5,
            }}
            startIcon={
              downloading && <CircularProgress size={16} color="inherit" />
            }
          >
            {downloading ? "Downloading..." : "Download"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DownloadExcelModal;
